import React from 'react';
import { ApolloProvider, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import { GET_ONBOARDING_CONTEXT, COMPLETE_MODERNIZED_ONBOARDING } from '../graphql/modernizedMotivationOnboarding.gql.js';
import Modal from '../components/ModernizedMotivationOnboarding/Modal.jsx';
import StepMotivation from '../components/ModernizedMotivationOnboarding/StepMotivation.jsx';
import StepGoals from '../components/ModernizedMotivationOnboarding/StepGoals.jsx';
import StepTimeline from '../components/ModernizedMotivationOnboarding/StepTimeline.jsx';
import StepWelcomeBack from '../components/ModernizedMotivationOnboarding/StepWelcomeBack.jsx';
import { buildWelcomeBackContent } from '../components/ModernizedMotivationOnboarding/welcomeBackContent';

// Switching to a different motivation must discard goals selected under the
// previous direction — they belong to the other category and would otherwise be
// submitted together, producing a contradictory buyer+seller goal set. Re-selecting
// the same motivation keeps in-progress picks. Exported for unit testing.
export function goalsAfterMotivationChange(current, next, selected) {
  return next === current ? selected : [];
}

export function Inner() {
  const { data, loading, error, refetch } = useQuery(GET_ONBOARDING_CONTEXT, { fetchPolicy: 'network-only' });
  const [complete, { loading: saving }] = useMutation(COMPLETE_MODERNIZED_ONBOARDING, {
    // GetOnboardingContext keeps the modal's own gate up-to-date; GetSuccessionPlanStatus
    // is what the LaunchPad's ApprovalStatusBanner reads from, so refresh it too — that's
    // what makes "Profile Under Review" appear without a manual page reload.
    refetchQueries: ['GetOnboardingContext', 'GetSuccessionPlanStatus'],
    awaitRefetchQueries: true,
  });

  const [step, setStep] = React.useState(1);
  const [motivation, setMotivation] = React.useState(null);
  const [selectedGoals, setSelectedGoals] = React.useState([]);
  const [timeline, setTimeline] = React.useState(null);
  const [approvalConfirmation, setApprovalConfirmation] = React.useState(false);
  // Welcome-back intro ([C]). One-way: flips true once the gate passes with the
  // toggle on, false-forward when the user proceeds. Never re-shown.
  const [showWelcome, setShowWelcome] = React.useState(false);

  const user = data?.getCurrentUser;
  const company = user?.company;
  const isCompleted = user?.modernized_onboarding_completed;
  // Denied users do NOT see the modal again — per the Rejection Policy. Their retry
  // surface is /profile (existing transition-goals edit path). This matches the
  // existing ~35-user production pattern where denied users retain their current
  // state and can only re-request via /profile.
  const isDenied = user?.approval_state === 'denied';

  // Gate: only show if not completed and feature flags are present on company.
  // `modernized_onboarding_completed` is the sole gate — it's flipped to true
  // on submit (both pending and direct-apply branches in the mutation), so a
  // user with an in-flight request normally won't see the modal. If an admin
  // explicitly resets that flag, the intent is to let the user redo the flow,
  // so we honor that even if a stale pending intent is still on the user.
  // Duplicate-submit protection is enforced server-side in the mutation.
  const features = (company?.feature_types || []).map(String);
  const flowEnabled = features.includes('modernized_onboarding_home_page') && features.includes('modernized_motivation_onboarding');
  // [C] welcome-back intro toggle. Only meaningful when the modal itself renders
  // (flowEnabled). The manager-approval FYI is shown only where a change can
  // actually go pending — i.e. an approval workflow is enabled (see [A]).
  const welcomeEnabled = features.includes('modernized_onboarding_welcome_back');
  const approvalWorkflowEnabled =
    features.includes('seller_approval_process') || features.includes('successor_approval_process');

  // Use global company goals and filter by motivation against known lists
  const allGoals = (company?.goals || []).map(g => ({ id: String(g.id), name: g.name }));
  const growthNames = ["Buying", "Merging", "Partnering", "Become a Successor", "Forming a Partnership", "Finding a Junior Partner", "Becoming a Junior Partner"];
  const successionNames = ["Find a Successor", "Selling my practice", "Selling part of my practice"];
  const goalOptions = React.useMemo(() => {
    if (motivation === 'growth') return allGoals.filter(g => growthNames.includes(g.name));
    if (motivation === 'succession') return allGoals.filter(g => successionNames.includes(g.name));
    return [];
  }, [motivation, allGoals]);

  // Show the welcome screen first, once data has loaded and both the modal flow
  // and the welcome toggle are on. Done in an effect (not lazy useState init)
  // because flowEnabled depends on the query result, which is undefined on the
  // first (loading) render.
  React.useEffect(() => {
    if (flowEnabled && welcomeEnabled) setShowWelcome(true);
  }, [flowEnabled, welcomeEnabled]);

  if (loading) return null;
  if (error) return null;
  if (isCompleted || isDenied || !flowEnabled) return null;

  // Did the company enable either approval toggle? Determines whether the
  // current submit will trigger the approval workflow (drives confirmation
  // copy on success) — except for `no_motivation`, which always opts out.
  const approvalRequired =
    motivation !== 'no_motivation' &&
    (features.includes('seller_approval_process') || features.includes('successor_approval_process'));

  const toggleGoal = (id) => {
    setSelectedGoals((prev) => prev.includes(id) ? prev.filter(x => x !== id) : prev.concat(id));
  };

  // Changing direction must reset the goal selection. `goalOptions` is filtered by
  // `motivation`, but `selectedGoals` is not — so without this, goals checked under
  // the previous motivation (e.g. Growth) survive a switch to Succession and get
  // submitted together, producing a contradictory buyer+seller goal set.
  const handleMotivationChange = (next) => {
    setSelectedGoals((prev) => goalsAfterMotivationChange(motivation, next, prev));
    setMotivation(next);
  };

  const handleSubmit = async () => {
    try {
      if (typeof window !== 'undefined' && window.ahoy && typeof window.ahoy.track === 'function') {
        window.ahoy.track('modernized_onboarding_motivation_submitted', {
          motivation,
          approval_required: approvalRequired,
        });
      }
      const variables = {
        motivation,
        retirementTimelineBucket: timeline,
      };
      if (motivation !== 'no_motivation') variables.transitionGoalIds = selectedGoals;
      const { data: result } = await complete({ variables });
      const wasPending = result?.completeModernizedOnboarding?.approval_state === 'pending';
      if (wasPending) {
        // Show the approval-sent confirmation panel; LaunchPad's existing
        // ApprovalStatusBanner will pick up the pending state on next render.
        setApprovalConfirmation(true);
      } else {
        // Legacy direct-apply path: refetch already ran via Apollo, modal
        // closes via the gate check on next render.
        await refetch();
      }
    } catch (e) {
      // no-op
    }
  };

  return (
    <Modal>
      {showWelcome && !approvalConfirmation && (
        <StepWelcomeBack
          brandedName={company?.branded_name}
          showApprovalFyi={approvalWorkflowEnabled}
          onContinue={() => setShowWelcome(false)}
        />
      )}
      {!showWelcome && step === 1 && !approvalConfirmation && (
        <StepMotivation
          value={motivation}
          onChange={handleMotivationChange}
          onNext={() => setStep(motivation === 'no_motivation' ? 3 : 2)}
          fyiNote={welcomeEnabled && approvalWorkflowEnabled ? buildWelcomeBackContent(company?.branded_name).fyi : null}
        />
      )}
      {step === 2 && !approvalConfirmation && (
        <StepGoals
          options={goalOptions}
          selected={selectedGoals}
          onToggle={toggleGoal}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
          businessType={company?.business_type}
        />
      )}
      {step === 3 && (
        <StepTimeline
          value={timeline}
          onChange={setTimeline}
          onBack={() => setStep(motivation === 'no_motivation' ? 1 : 2)}
          onSubmit={handleSubmit}
          saving={saving}
          approvalRequired={approvalRequired}
          approvalConfirmation={approvalConfirmation}
        />
      )}
    </Modal>
  );
}

export default function ModernizedMotivationOnboardingContainer() {
  return (
    <ApolloProvider client={client}>
      <Inner />
    </ApolloProvider>
  );
}
